// Btn primitive — port of design/v3/Primitives.jsx (Btn) with size/variant API preserved.

import type { ButtonHTMLAttributes, CSSProperties, ReactNode } from 'react';

export type BtnVariant =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'danger'
  | 'strong';
export type BtnSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: BtnVariant;
  size?: BtnSize;
  children?: ReactNode;
}

const sizeStyle: Record<BtnSize, CSSProperties> = {
  sm: { padding: '5px 10px', fontSize: 12 },
  md: { padding: '8px 14px', fontSize: 13 },
  lg: { padding: '10px 18px', fontSize: 14 },
};

const variantStyle: Record<BtnVariant, CSSProperties> = {
  primary: {
    background: 'var(--primary)',
    color: 'var(--on-primary)',
    border: '1px solid var(--primary)',
    fontWeight: 600,
  },
  secondary: {
    background: 'var(--bg-surface)',
    color: 'var(--fg-1)',
    border: '1px solid var(--border-base)',
  },
  ghost: {
    background: 'transparent',
    color: 'var(--primary-strong)',
    border: '1px solid transparent',
  },
  danger: {
    background: 'var(--danger)',
    color: '#fff',
    border: '1px solid var(--danger)',
  },
  strong: {
    background: 'var(--primary-strong)',
    color: '#fff',
    border: '1px solid var(--primary-strong)',
  },
};

export function Button({
  variant = 'primary',
  size = 'md',
  style,
  disabled,
  children,
  type,
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      type={type ?? 'button'}
      disabled={disabled}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        fontFamily: 'var(--font-sans)',
        fontWeight: 500,
        borderRadius: 'var(--radius-md)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'all 180ms var(--ease-out)',
        opacity: disabled ? 0.5 : 1,
        lineHeight: 1.2,
        textAlign: 'center',
        maxWidth: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        overflowWrap: 'anywhere',
        ...sizeStyle[size],
        ...variantStyle[variant],
        ...style,
      }}
    >
      {children}
    </button>
  );
}
